/**
 * Customer / store detail (Wave-4 console). Opened from a clickable Customers or
 * Stores row. Mockup layout: KPI stat cards → facts (owner / seats / devices /
 * last active) → LICENSE (facts + super-admin edit) → DEVICES (activated devices,
 * each "Manage"-able) → live processes, with a sticky action footer (block/unblock,
 * revoke/restore activation). Server enforces (006/009/010).
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useServices } from "../app/context.js";
import type { CatalogEdition, CustomerDetail, DeviceDetail, PolicyView } from "@genz/platform";
import { ActionBar } from "../ui/ActionBar.js";
import { Drawer } from "../ui/Drawer.js";
import { DeviceDetailPanel } from "./DeviceDetailPanel.js";
import { CopyButton } from "../ui/CopyButton.js";
import { enumLabel } from "../format/labels.js";
import {
  AssignLicenseInline,
  DeviceTable,
  FactRows,
  LicenseEditForm,
  LicenseRenewControls,
  SectionLabel,
  StatCards,
  StatusBadge,
  fmtDate,
  fmtDateTime,
  fmtNum,
  fmtRelative,
} from "./detail-shared.js";

/** Known pricing tiers — mirrors cloud `entitlements.service.TIERS`; the PATCH
 *  endpoint rejects anything else with 400. */
const TIERS = ["free", "pro", "enterprise"] as const;

export function CustomerDetailPanel({
  customerId,
  onChanged,
}: {
  customerId: string;
  onChanged?: () => void;
}) {
  const { client } = useServices();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editLicense, setEditLicense] = useState(false);
  const [deviceFor, setDeviceFor] = useState<DeviceDetail | null>(null);
  const [edition, setEdition] = useState<CatalogEdition>("brand");
  const [tier, setTier] = useState<string>("free");
  const [aiLimit, setAiLimit] = useState<string>("");
  const [allowedViews, setAllowedViews] = useState<string>("");
  // Real resolvable views for this workspace — used to validate `allowed_views`
  // against actual options instead of accepting free-text typos.
  const [resolvedViews, setResolvedViews] = useState<string[] | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await client.customerDetail(customerId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, customerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Keep the per-customer controls in sync with the loaded customer.
  useEffect(() => {
    if (detail) {
      setEdition(detail.customer.edition);
      setTier(detail.customer.tier);
      setAiLimit(detail.customer.ai_monthly_limit == null ? "" : String(detail.customer.ai_monthly_limit));
      setAllowedViews((detail.customer.allowed_views ?? []).join(", "));
    }
  }, [detail]);

  // Load the real resolvable views once so `allowed_views` can be validated against
  // actual options. Best-effort: a failure just falls back to trim-only normalization.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const opts = await client.templateOptions();
        if (alive) setResolvedViews(opts.views.map((v: PolicyView) => v.view));
      } catch {
        if (alive) setResolvedViews(null);
      }
    })();
    return () => { alive = false; };
  }, [client]);

  // Parse the comma list once; flag entries that aren't real resolvable views.
  const parsedViews = useMemo(
    () => allowedViews.split(",").map((s) => s.trim()).filter(Boolean),
    [allowedViews],
  );
  const unknownViews = useMemo(() => {
    if (!resolvedViews || resolvedViews.length === 0) return [];
    const known = new Set(resolvedViews);
    return parsedViews.filter((v) => !known.has(v));
  }, [parsedViews, resolvedViews]);

  /**
   * GEN-8: "Block account" and "Revoke license" fired on the FIRST click — one
   * mis-click cut off a live paying customer and every one of their devices with
   * no undo prompt. Every other destructive action in the console already
   * confirms (DeviceDetailPanel.revoke, LicenseDetailPanel.remove,
   * ImageLibraryTab.remove); this is that same guard, worded for the blast radius.
   * Un-blocking / restoring is not destructive, so it stays one click.
   */


  /**
   * The page commit (WS-Z5, Direction B rule 7).
   *
   * Edition, Tier and "Limits & allowed views" were three `.detail-inset` blocks with three
   * `.btn--sm.btn--primary` Saves, all three calling `client.updateCustomer(id, …)` with a
   * different slice of the same record. Three loud invitations, one endpoint: the operator
   * who changed the tier AND the AI cap and pressed the Save next to the tier wrote one and
   * walked away from the other. One `Save changes`, in the bar at the bottom, sends the
   * fields that actually differ from the loaded customer.
   */
  function pendingPatch(): Record<string, unknown> | null {
    if (!detail) return null;
    const c = detail.customer;
    const patch: Record<string, unknown> = {};
    if (edition !== c.edition) patch.edition = edition;
    if (tier !== c.tier) patch.tier = tier;
    const limitNow = c.ai_monthly_limit == null ? "" : String(c.ai_monthly_limit);
    if (aiLimit.trim() !== limitNow)
      Object.assign(patch, aiLimit.trim() === "" ? { clear_ai_limit: true } : { ai_monthly_limit: Number(aiLimit) });
    const viewsNow = (c.allowed_views ?? []).join(", ");
    if (allowedViews.trim() !== viewsNow.trim())
      Object.assign(patch, parsedViews.length === 0 ? { clear_allowed_views: true } : { allowed_views: parsedViews });
    return Object.keys(patch).length === 0 ? null : patch;
  }

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (loading && !detail) {
    return <p className="faint" data-testid="customer-detail-loading">Loading customer…</p>;
  }
  if (!detail) {
    return (
      <div data-testid="customer-detail-empty">
        <p role="alert" className="faint">{error ?? "Couldn’t load this customer’s details."}</p>
        <button type="button" className="btn btn--sm" onClick={() => void refresh()}>Retry</button>
      </div>
    );
  }

  const { customer, license, activation_status, kpis } = detail;
  const devices = detail.devices ?? [];
  const live = detail.live ?? [];
  const blocked = customer.status === "blocked";
  const liveNow = live.filter((p) => p.online).length;
  // "Last active" = most recent of any live heartbeat / device last-seen.
  const lastActive = [
    ...live.map((p) => p.server_ts),
    ...devices.map((d) => d.last_seen_at),
  ].filter(Boolean).sort().pop() ?? null;

  /* The bar is a SIBLING of `.detail` (Z0 fix round P-2): `.drawer__body` is a scrolling
     column with rule 7's sticky bar over it, and the shared clearance that keeps a focused
     control out from behind the bar comes from `:where(:has(> .actionbar))` — a DIRECT-child
     selector. One wrapper element and the column reserves nothing. */
  return (
    <>
    <div className="detail" data-testid="customer-detail">
      {kpis && (
        <StatCards
          cards={[
            { label: "Generated", value: fmtNum(kpis.designs_generated), sub: "all-time", testid: "kpi-designs" },
            { label: "Mock-ups", value: fmtNum(kpis.views_rendered), sub: "all-time", testid: "kpi-views" },
            { label: "Jobs", value: fmtNum(kpis.jobs_total), testid: "kpi-jobs" },
            { label: "Live now", value: liveNow, sub: "desktops", testid: "kpi-running" },
          ]}
        />
      )}

      <FactRows
        testid="customer-facts"
        rows={[
          { label: "Owner", value: customer.email },
          { label: "Role", value: customer.role },
          { label: "Tier", value: <span className="badge badge--info">{enumLabel(customer.tier)}</span> },
          { label: "Edition", value: <span className="badge badge--info">{customer.edition}</span> },
          { label: "Seats", value: license ? <span className="num">{license.seats_used} / {license.seats}</span> : "—" },
          { label: "Devices", value: <span className="num">{devices.length}</span> },
          { label: "Last active", value: fmtRelative(lastActive) },
        ]}
      />

      {/* Rule 8: the three edit blocks are `.inspector`s — a bordered panel with a 12/500
          header row — not `.detail-inset` boxes that re-drew the same frame locally. Their
          three Saves are one commit in the action bar at the bottom (rule 7). */}
      <div className="inspector" data-testid="customer-plan-edit">
        <div className="inspector__head">Edition</div>
        <div className="inspector__body">
        <label className="field">
          <span>Edition <span className="faint">(catalog access + customization)</span></span>
          <select
            value={edition}
            onChange={(e) => setEdition(e.target.value as CatalogEdition)}
            data-testid="edition-select"
          >
            <option value="brand">Brand — shared catalog + limited customization</option>
            <option value="studio">Studio — private/exclusive + full customization</option>
          </select>
        </label>
        </div>
      </div>

      <div className="inspector" data-testid="customer-tier-edit">
        <div className="inspector__head">Tier</div>
        <div className="inspector__body">
        <label className="field">
          <span>Pricing tier <span className="faint">(caps entitlements)</span></span>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            data-testid="tier-select"
          >
            {(TIERS.includes(tier as (typeof TIERS)[number]) ? TIERS : [tier, ...TIERS]).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        </div>
      </div>

      <div className="inspector" data-testid="customer-limits-edit">
        <div className="inspector__head">Limits &amp; allowed views</div>
        <div className="inspector__body">
        <label className="field">
          <span>AI monthly limit <span className="faint">(blank = use plan default)</span></span>
          <input
            type="number"
            min={0}
            value={aiLimit}
            placeholder="plan default"
            onChange={(e) => setAiLimit(e.target.value)}
            data-testid="ai-limit-input"
          />
        </label>
        <label className="field">
          <span>
            Allowed views <span className="faint">(comma — blank = all)</span>
            {resolvedViews && resolvedViews.length > 0 && (
              <span className="faint"> · known: {resolvedViews.join(", ")}</span>
            )}
          </span>
          <input
            type="text"
            value={allowedViews}
            placeholder={resolvedViews && resolvedViews.length > 0 ? resolvedViews.join(", ") : "back, front, floating"}
            onChange={(e) => setAllowedViews(e.target.value)}
            list={resolvedViews && resolvedViews.length > 0 ? "allowed-views-known" : undefined}
            aria-invalid={unknownViews.length > 0}
            data-testid="allowed-views-input"
          />
          {resolvedViews && resolvedViews.length > 0 && (
            <datalist id="allowed-views-known">
              {resolvedViews.map((v) => <option key={v} value={v} />)}
            </datalist>
          )}
        </label>
        {unknownViews.length > 0 && (
          <p role="alert" className="faint" data-testid="allowed-views-warning">
            Not a known view: {unknownViews.join(", ")}. Pick from {resolvedViews!.join(", ")}.
          </p>
        )}
        </div>
      </div>

      <SectionLabel>License</SectionLabel>
      {license ? (
        <>
          <div className="license-card" data-testid="customer-license-card">
            <div>
              <strong className="license-card__key id-inline">
                <code>{license.license_key_masked}</code>
                <CopyButton value={license.license_key_masked} label="Copy license key" />
              </strong>
              <p className="faint">
                {enumLabel(license.tier)} · {license.expires_at ? `valid until ${fmtDate(license.expires_at)}` : "perpetual"}
              </p>
            </div>
            <div className="row row--tight">
              <StatusBadge status={license.expired ? "expired" : "active"} />
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => setEditLicense((v) => !v)}
                data-testid="customer-license-edit-toggle"
                aria-expanded={editLicense}
              >
                {editLicense ? "Close" : "Manage"}
              </button>
            </div>
          </div>
          {editLicense && (
            <div className="inspector" data-testid="customer-license-edit">
              <div className="inspector__head">License</div>
              <div className="inspector__body">
                <LicenseEditForm license={license} onSaved={() => void refresh()} />
                <LicenseRenewControls license={license} onSaved={() => void refresh()} />
              </div>
            </div>
          )}
        </>
      ) : (
        <AssignLicenseInline
          customerId={customer.id}
          tenantId={customer.tenant_id}
          onAssigned={() => void refresh()}
        />
      )}

      <SectionLabel>Activated devices ({devices.length})</SectionLabel>
      {/* WS-Z9: the shared `DeviceTable`, not a third hand-rolled card list. It carries every
          datum these cards did — and the grace window they dropped — at the dense table's
          row height, which is what Direction B's rule 6 asks an operator console for. */}
      <DeviceTable
        devices={devices}
        onManage={(d) => setDeviceFor(d)}
        manageTestId="customer-device-manage"
      />

      {live.length > 0 && (
        <>
          <SectionLabel>Live processes</SectionLabel>
          <ul className="detail-live" data-testid="detail-live">
            {live.map((p) => (
              <li key={p.device_id} data-testid="detail-live-row">
                <span className={`badge ${p.online ? "badge--success" : "badge--neutral"}`}>
                  {p.online ? "online" : "offline"}
                </span>{" "}
                {p.active_job_id
                  ? `running ${p.active_job_id.slice(0, 8)} (${Math.round((p.progress ?? 0) * 100)}%)`
                  : "idle"}
                {" · "}
                <span className="faint">{p.app_version ?? "?"} · {fmtDateTime(p.server_ts)}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {error && <p role="alert" data-testid="customer-detail-error">{error}</p>}
    </div>

      {/* Rule 7 — every page-level action for this customer, in the bar, with the ONE primary
          last. "Unblock account" used to BE the primary when the account was blocked, which
          made the loudest control on the page change identity with the customer's status. */}
      <ActionBar
        label="Customer actions"
        primary={
          <button
            type="button"
            className="btn btn--primary"
            /* `unknownViews` blocks the WHOLE commit, and that is a widening the merge
               introduced (fix round 1, L-3). It used to disable `limits-save` only — the one
               of the three retired Saves that sent `allowed_views` — so a typo there left the
               Edition and Tier Saves working. There is one Save now and it is all-or-nothing:
               a typo in Allowed views holds back an unrelated Edition change until it is
               fixed. That is the safer half of the trade (the server 422s the bad set, and the
               operator sees the reason beside the field that caused it rather than a rejected
               write of three fields), and it is stated here because a widened block that
               nobody wrote down is how a merged commit loses a capability quietly. */
            disabled={busy === "save" || unknownViews.length > 0 || pendingPatch() === null}
            data-testid="customer-save"
            onClick={() => {
              const patch = pendingPatch();
              if (!patch) return;
              void run("save", () => client.updateCustomer(customer.id, patch));
            }}
          >
            {busy === "save" ? "Saving…" : "Save changes"}
          </button>
        }
      >
        <button
          type="button"
          className={`btn ${blocked ? "" : "btn--danger"}`}
          disabled={busy === "block"}
          data-testid="detail-block"
          onClick={async () => {
            if (blocked) {
              void run("block", () => client.unblockCustomer(customer.id));
              return;
            }
            if (!await confirmAction(
              `Block ${customer.email}? They are signed out everywhere and cannot use the app until unblocked.`,
            )) return;
            void run("block", () => client.blockCustomer(customer.id));
          }}
        >
          {blocked ? "Unblock account" : "Block account"}
        </button>
        {activation_status === "active" ? (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy === "revoke"}
            data-testid="detail-revoke"
            onClick={async () => {
              if (!await confirmAction(
                `Revoke ${customer.email}’s license? All ${devices.length} activated device(s) stop working immediately.`,
              )) return;
              void run("revoke", () => client.revokeCustomerLicense(customer.id));
            }}
          >
            Revoke license
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy === "revoke" || activation_status === "inactive"}
            data-testid="detail-restore"
            onClick={() => run("revoke", () => client.restoreCustomerLicense(customer.id))}
          >
            Restore license
          </button>
        )}
      </ActionBar>

      {deviceFor && (
        <Drawer
          title={deviceFor.label ?? deviceFor.device_id}
          subtitle="Device"
          badge={<StatusBadge status={deviceFor.active ? "active" : "grace_lapsed"} />}
          onClose={() => setDeviceFor(null)}
          testid="device-detail-drawer"
        >
          <DeviceDetailPanel
            device={deviceFor}
            customerLabel={customer.email}
            onChanged={() => void refresh()}
            onClose={() => setDeviceFor(null)}
          />
        </Drawer>
      )}
    </>
  );
}
